/**
 * weekLabels.js  --  central rule for converting an internal week number
 * into the display label users see, plus typed conversions between the
 * codebase's TWO co-existing week numbering conventions.
 *
 * ABSOLUTE convention (used by reading.week, worksheet.week, the SW URL
 * `/self-work/{kind}/{week}`, the readings URL `/readings/week-N`, and the
 * `weeks` map in ContentLibraryPage):
 *   - week 1  / week 2          =>  2 Week Window
 *   - week 3 .. week 32         =>  30 Programme weeks (1..30)
 *
 * BACKEND convention (used by user.current_week on the user document and
 * by db.weekly_banners.week):
 *   - -2 / -1                   =>  2 Week Window weeks 1 and 2
 *   - 0                         =>  2 Week Window complete (transitional)
 *   - 1..30                     =>  30 Programme weeks
 *
 * Convert between the two with `absoluteFromUserCurrentWeek` and
 * `userCurrentWeekFromAbsolute`.  All `weekLabel` / `bareWeekLabel` /
 * `programmeWeekNumber` helpers below assume the ABSOLUTE convention.
 *
 * This file is the JS twin of /app/backend/week_utils.py — both must stay
 * in lock-step.
 */

export const TWO_WEEK_WINDOW = [1, 2];
export const FIRST_PROGRAMME_WEEK = 3;
export const LAST_PROGRAMME_WEEK = 32;
export const PROGRAMME_WEEK_COUNT = 30;
export const TWO_WEEK_WINDOW_LENGTH = 2;

export const isTwoWeekWindow = (internalWeek) =>
  internalWeek === 1 || internalWeek === 2;

export const isProgrammeWeek = (internalWeek) =>
  internalWeek >= FIRST_PROGRAMME_WEEK && internalWeek <= LAST_PROGRAMME_WEEK;

/**
 * Internal week (3..32) -> Programme week (1..30).  Returns 0 for the 2WW.
 */
export const programmeWeekNumber = (internalWeek) =>
  isProgrammeWeek(internalWeek) ? internalWeek - 2 : 0;

/**
 * Convert a BACKEND-convention week value (user.current_week:
 *   -2/-1 = 2 Week Window;  0 = window complete;  1..30 = programme weeks)
 * to the ABSOLUTE convention used elsewhere on the frontend (1..2 = 2WW,
 * 3..32 = programme weeks).
 *
 * Returns 0 when the user is in the transitional "window complete" state.
 */
export function absoluteFromUserCurrentWeek(backendWeek) {
  if (backendWeek === 0 || backendWeek == null) return 0;
  if (backendWeek <= -1) return backendWeek + 3;        // -2 -> 1, -1 -> 2
  return backendWeek + 2;                                // 1..30 -> 3..32
}

/**
 * Convert an ABSOLUTE-convention week to the BACKEND convention used by
 * user.current_week.  Useful when writing back to the user document.
 */
export function userCurrentWeekFromAbsolute(absoluteWeek) {
  if (absoluteWeek === 0 || absoluteWeek == null) return 0;
  if (absoluteWeek <= 2) return absoluteWeek - 3;        // 1 -> -2, 2 -> -1
  return absoluteWeek - 2;                                // 3..32 -> 1..30
}

/**
 * Full eyebrow label, e.g.
 *   weekLabel(1)               -> "2 Week Window  ·  Week 1"
 *   weekLabel(3)               -> "Programme  ·  Week 1"
 *   weekLabel(3, 'lesson')     -> "Lesson  ·  Week 1"
 *   weekLabel(32)              -> "Programme  ·  Week 30"
 */
export function weekLabel(internalWeek, kind = 'programme') {
  if (isTwoWeekWindow(internalWeek)) {
    return `Personal History  ·  Week ${internalWeek} of 2`;
  }
  const pw = programmeWeekNumber(internalWeek);
  if (kind === 'lesson') return `Lesson  ·  Week ${pw}`;
  return `Programme  ·  Week ${pw}`;
}

/**
 * Short label without the kind prefix, e.g.
 *   shortWeekLabel(1)   -> "Personal History Week 1 of 2"
 *   shortWeekLabel(3)   -> "Programme Week 1 of 30"
 *   shortWeekLabel(32)  -> "Programme Week 30 of 30"
 */
export function shortWeekLabel(internalWeek) {
  if (isTwoWeekWindow(internalWeek)) {
    return `Personal History Week ${internalWeek} of 2`;
  }
  const pw = programmeWeekNumber(internalWeek);
  if (pw === 0) return `Week ${internalWeek}`;
  return `Programme Week ${pw} of ${PROGRAMME_WEEK_COUNT}`;
}

/**
 * Just the programme-week digit, e.g. "Week 1" / "Week 30" — for placement
 * inside larger sentences ("Complete Week 1 to unlock Week 2.").  Falls back
 * to the internal number for non-programme weeks so the sentence still
 * reads correctly during the 2WW.
 */
export function bareWeekLabel(internalWeek) {
  if (isTwoWeekWindow(internalWeek)) return `Personal History Week ${internalWeek}`;
  const pw = programmeWeekNumber(internalWeek);
  return `Programme Week ${pw}`;
}
